import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { parseApiError } from 'shared/api/apiError'
import { isAxiosError } from 'shared/api/isAxiosError'
import notifyServerError from 'shared/api/notifyServerError'
import reportApiFailure from 'shared/api/reportApiFailure'
import { useConfirm } from '../../../shared/components/confirm/ConfirmProvider'
import { deleteNewsAttachment } from '../api/deleteNewsAttachment'
import { uploadNewsAttachment } from '../api/uploadNewsAttachment'
import {
  MAX_NEWS_ATTACHMENTS, NewsAttachmentRejection, rejectNewsAttachment,
} from '../models/newsAttachments'
import { newsErrorMessage } from '../models/newsErrorMessage'
import {
  NewsAttachment, NewsPost, NewsPostCreateRequest, NewsPostUpdateRequest,
} from '../models/types'

export type NewsUploadFailure = 'rejected' | 'uploadFailed'

export interface ChosenNewsFile {
  key: string
  file: File
  uploadFailure: NewsUploadFailure | null
}

// How the body relates to the chosen files: images pasted into the text are chosen files too, and
// the body is rebuilt with references to them once they are uploaded.
export interface NewsInlineImages {
  isInline: (key: string) => boolean
  onUploaded: (key: string, attachment: NewsAttachment) => void
  // The body to store, with every uploaded image as a reference into the post (null while creating).
  getBody: (postId: number | null) => string
}

interface UseNewsPostSaveParams {
  post: NewsPost | null
  onCreate: (draft: NewsPostCreateRequest) => Promise<NewsPost>
  onUpdate: (id: number, draft: NewsPostUpdateRequest) => Promise<NewsPost | null>
  onInvalidate: () => Promise<void>
  inlineImages: NewsInlineImages
}

// Skipped once the token is gone: a forced logout clears it before redirecting to the login page,
// and the redirect must not be held up by a leave prompt.
const warnBeforeUnload = (event: BeforeUnloadEvent) => {
  if (localStorage.getItem('accessToken') === null) {
    return
  }
  event.preventDefault()
  // eslint-disable-next-line no-param-reassign
  event.returnValue = ''
}

function useNewsPostSave({
  post, onCreate, onUpdate, onInvalidate, inlineImages,
}: UseNewsPostSaveParams) {
  const { t } = useTranslation()
  const { confirm } = useConfirm()
  const [savedPost, setSavedPost] = useState<NewsPost | null>(post)
  const [keptAttachments, setKeptAttachments] = useState<NewsAttachment[]>(post?.attachments ?? [])
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<number[]>([])
  const [chosenFiles, setChosenFiles] = useState<ChosenNewsFile[]>([])
  const [isSaving, setSaving] = useState<boolean>(false)
  const [isGone, setGone] = useState<boolean>(false)
  const nextKeyRef = useRef(0)
  const stoppedRef = useRef(false)
  const abortRef = useRef<AbortController | null>(null)
  const hasPendingUploadsRef = useRef(false)

  // Derived rather than stored: the limit check depends on the kept attachments, which change
  // whenever the admin removes one.
  const rejections = useMemo<(NewsAttachmentRejection | null)[]>(() => {
    const allowedNewCount = MAX_NEWS_ATTACHMENTS - keptAttachments.length
    return chosenFiles.map((chosen, index) => rejectNewsAttachment(chosen.file, index, allowedNewCount))
  }, [chosenFiles, keptAttachments.length])
  const hasRejection = rejections.some((rejection) => rejection !== null)

  const stop = useCallback(() => {
    stoppedRef.current = true
    hasPendingUploadsRef.current = false
    abortRef.current?.abort()
    window.removeEventListener('beforeunload', warnBeforeUnload)
  }, [])

  useEffect(() => stop, [stop])

  const addFiles = (files: File[]): ChosenNewsFile[] => {
    const added = files.map((file) => {
      nextKeyRef.current += 1
      return { key: `file-${nextKeyRef.current}`, file, uploadFailure: null }
    })
    if (added.length > 0) {
      setChosenFiles((current) => [...current, ...added])
    }
    return added
  }

  const removeChosenFile = (key: string) => {
    setChosenFiles((current) => current.filter((chosen) => chosen.key !== key))
  }

  const removeKeptAttachment = (id: number) => {
    setKeptAttachments((current) => current.filter((attachment) => attachment.id !== id))
    setRemovedAttachmentIds((current) => [...current, id])
  }

  const savePost = async (title: string, shouldNotify: boolean): Promise<NewsPost | null | undefined> => {
    try {
      if (savedPost === null) {
        // Confirmed before the post is written, but a refusal cancels only the e-mail notification.
        const sendNotification = shouldNotify ? await confirm({ message: t('news.notifyConfirm') }) : false
        return await onCreate({ title, body: inlineImages.getBody(null), sendNotification })
      }
      return await onUpdate(savedPost.id, { title, body: inlineImages.getBody(savedPost.id) })
    } catch (error) {
      if (!isAxiosError(error)) {
        throw error
      }
      return undefined
    }
  }

  // Sends the pending removals before the post: the uploads that follow count on the freed slots.
  // A 404 counts as removed — the attachment, or the whole post, is already gone.
  const removeAttachments = async (postId: number) => {
    let hasRemoved = false
    for (const id of removedAttachmentIds) {
      if (stoppedRef.current) {
        return { isStopped: true, hasRemoved }
      }

      try {
        // eslint-disable-next-line no-await-in-loop
        await deleteNewsAttachment(postId, id)
        hasRemoved = true
      } catch (error) {
        const apiError = parseApiError(error)
        if (apiError.status !== 404) {
          if (!stoppedRef.current && !axios.isCancel(error) && apiError.status !== 401) {
            reportApiFailure(error, () => toast.error(t(newsErrorMessage(apiError))))
          }
          return { isStopped: true, hasRemoved }
        }
      }
      setRemovedAttachmentIds((current) => current.filter((candidate) => candidate !== id))
    }
    return { isStopped: false, hasRemoved }
  }

  // Returns whether the loop was stopped before it went through every file, and which files landed.
  const uploadFiles = async (target: NewsPost, files: ChosenNewsFile[]) => {
    const uploadedKeys: string[] = []
    for (const chosen of files) {
      if (stoppedRef.current) {
        return { isStopped: true, uploadedKeys }
      }

      const controller = new AbortController()
      abortRef.current = controller
      try {
        // Sequential on purpose: the server lists attachments in the order they arrive.
        // eslint-disable-next-line no-await-in-loop
        const attachment = await uploadNewsAttachment(target.id, chosen.file, { signal: controller.signal })
        uploadedKeys.push(chosen.key)
        inlineImages.onUploaded(chosen.key, attachment)
        setChosenFiles((current) => current.filter((candidate) => candidate.key !== chosen.key))
        setKeptAttachments((current) => [...current, attachment])
      } catch (error) {
        const apiError = parseApiError(error)
        const { status } = apiError
        if (stoppedRef.current || axios.isCancel(error) || status === 401) {
          return { isStopped: true, uploadedKeys }
        }
        if (status === 403 || status === 404) {
          toast.error(t(status === 403 ? 'news.errorForbidden' : 'news.errorNotFound'))
          setGone(status === 404)
          return { isStopped: true, uploadedKeys }
        }

        if (apiError.reason === 'serverError' && apiError.traceId) {
          notifyServerError(apiError.traceId)
        }
        const failure: NewsUploadFailure = status === 400 ? 'rejected' : 'uploadFailed'
        setChosenFiles((current) => current.map(
          (candidate) => (candidate.key === chosen.key ? { ...candidate, uploadFailure: failure } : candidate),
        ))
        toast.error(t(failure === 'rejected' ? 'news.attachmentRejectedFor' : 'news.attachmentUploadFailedFor', {
          filename: chosen.file.name,
        }))
      } finally {
        abortRef.current = null
      }
    }
    return { isStopped: false, uploadedKeys }
  }

  // Resolves whether the body now holds references to every uploaded inline image.
  const saveBodyReferences = async (target: NewsPost, title: string): Promise<boolean> => {
    try {
      const updated = await onUpdate(target.id, { title, body: inlineImages.getBody(target.id) })
      if (updated === null) {
        setGone(true)
        return false
      }
      if (!stoppedRef.current) {
        setSavedPost(updated)
        setKeptAttachments(updated.attachments)
      }
      return true
    } catch (error) {
      if (!isAxiosError(error)) {
        throw error
      }
      return false
    }
  }

  // Resolves true only when the post, every file and the text all saved — the form then closes.
  const save = async (title: string, shouldNotify: boolean): Promise<boolean> => {
    if (isSaving || isGone || hasRejection) {
      return false
    }

    const files = chosenFiles
    stoppedRef.current = false
    hasPendingUploadsRef.current = files.length > 0
    setSaving(true)
    if (files.length > 0) {
      window.addEventListener('beforeunload', warnBeforeUnload)
    }

    // The feed refreshes itself once the post save reaches the server; removals alone do not.
    let hasRemoved = false
    let isFeedRefreshed = false
    try {
      if (savedPost !== null && removedAttachmentIds.length > 0) {
        const removal = await removeAttachments(savedPost.id)
        hasRemoved = removal.hasRemoved
        if (removal.isStopped || stoppedRef.current) {
          return false
        }
      }

      const target = await savePost(title, shouldNotify)
      isFeedRefreshed = target !== undefined
      if (stoppedRef.current || target === undefined) {
        return false
      }
      if (target === null) {
        setGone(true)
        return false
      }

      setSavedPost(target)
      setKeptAttachments(target.attachments)

      const { isStopped, uploadedKeys } = files.length > 0
        ? await uploadFiles(target, files)
        : { isStopped: false, uploadedKeys: [] as string[] }
      // An image pasted into the text only gets its attachment id once uploaded, so the text is
      // saved again with the references. That save refreshes the feed itself.
      const needsBodySave = !isStopped && uploadedKeys.some(inlineImages.isInline)
      const isBodySaved = needsBodySave ? await saveBodyReferences(target, title) : true
      if (files.length > 0 && !needsBodySave) {
        await onInvalidate()
      }
      if (stoppedRef.current || isStopped || !isBodySaved) {
        return false
      }
      toast.success(t('news.postSaved'))
      return uploadedKeys.length === files.length
    } finally {
      if (hasRemoved && !isFeedRefreshed) {
        await onInvalidate()
      }
      // Also on an unexpected error, so the form never stays locked with the leave prompt armed.
      hasPendingUploadsRef.current = false
      window.removeEventListener('beforeunload', warnBeforeUnload)
      if (!stoppedRef.current) {
        setSaving(false)
      }
    }
  }

  // Resolves whether the form may close. Only a save with files still to upload asks first.
  const requestClose = async (): Promise<boolean> => {
    if (!isSaving || !hasPendingUploadsRef.current) {
      return true
    }
    const shouldStop = await confirm({ message: t('news.uploadsStopConfirm') })
    if (shouldStop) {
      stop()
    }
    return shouldStop
  }

  return {
    savedPost,
    keptAttachments,
    chosenFiles,
    rejections,
    hasRejection,
    isSaving,
    isGone,
    addFiles,
    removeChosenFile,
    removeKeptAttachment,
    save,
    requestClose,
  }
}

export default useNewsPostSave
